/** map from keystroke to symbols that will get substituted in */
export type KeyMap = Record<string, string[]>;

/** track the mapping from keystroke to custom orthography */
export class KeyToOrthoMapping {
  private all: KeyMap = {};
  /** map from typed-key to replacement symbol */
  private single: Record<string, string> = {};
  /** map from shift-key (like S) to a list of options to cycle between */
  private multi: KeyMap = {};

  getAll(): KeyMap { return this.all; }
  getSingle(): Record<string, string> { return this.single; }
  getMulti(): KeyMap { return this.multi; }

  /** assign symbol[s] to a keystroke */
  setAssignment(keystroke: string, symbols: string[]) {
    this.all[keystroke] = symbols;
    this.updateFromAll();
  }

  /** reorder by moving 'keystroke' up or down one item in the list */
  moveKey(keystroke: string, where: 'up' | 'down') {
    const keys = Object.getOwnPropertyNames(this.all);
    const idx = keys.findIndex(k => k === keystroke);
    if (idx === -1 || (where === 'up' && idx === 0) || (where === 'down' && idx === keys.length - 1)) {
      return;
    }

    // up: [0,idx-1) k [idx-1] [idx+1,...]
    // down: [0,idx) [idx+1] k [idx+2,...]
    const newKeys = where === 'up'
      ? keys.slice(0, idx - 1).concat(keystroke).concat(keys[idx - 1]).concat(keys.slice(idx + 1))
      : keys.slice(0, idx).concat(keys[idx + 1]).concat(keystroke).concat(keys.slice(idx + 2));

    // reorder
    const keymap: KeyMap = {};
    newKeys.forEach(k => keymap[k] = this.all[k]);
    this.setAll(keymap);
  }

  deleteKey(keystroke: string) {
    const all: KeyMap = {};
    const keys = Object.getOwnPropertyNames(this.all);
    for (const key of keys) {
      if (key !== keystroke) {
        all[key] = this.all[key];
      }
    }
    this.all = all;
    this.updateFromAll();
  }

  setAll(all: KeyMap) {
    this.all = all;
    this.updateFromAll();
  }

  clearAll() {
    this.all = {};
    this.updateFromAll();
  }

  /** update all internal maps, consolidating & removing when needed */
  private updateFromAll() {
    const all: KeyMap = {};
    const single: Record<string, string> = {};
    const multi: KeyMap = {};
    const keys = Object.getOwnPropertyNames(this.all);
    for (const key of keys) {
      const symbols = this.all[key];
      if (symbols.length === 1 && symbols[0] !== '') {
        all[key] = symbols;
        single[key] = symbols[0];
      } else if (symbols.length > 1) {
        all[key] = symbols;
        multi[key] = symbols;
      }
    }
    this.all = all;
    this.single = single;
    this.multi = multi;
  }
}
